// NotesApp source file: tag-cards.component.ts

import {
  Input,
  Output,
  EventEmitter,
  Component,
  HostListener,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { computed, inject, SimpleChanges } from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';

import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';

import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { Note, Tag, DataService } from '../../../_service/data.service';
import { LayoutService } from '../../../_service/layout.service';
import { FilterParams } from '../../../_service/filter.service';

type Card = {
  _id: string;
  Title: string;
  Type: string;
  TagId: string;
  SortOrder: number;
  isActive: boolean;
  isExpanded: boolean;
  isVisible: boolean;
  Color?: string;
};

type NoteCard = {
  NoteID: string;
  CardID: string;
  isActive: boolean;
};

type NoteIdentity = {
  _id?: string;
};

@Component({
  selector: 'app-tag-cards',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './tag-cards.component.html',
  styleUrls: ['./tag-cards.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class TagCardsComponent implements OnInit, OnChanges {
  @Input() notes: Note[] = [];
  @Input() tags: Tag[] = [];
  @Input() filter: FilterParams = {};
  @Input() filterTagIds: string[] | undefined;
  @Input() isSelectMode: boolean = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() clickTagAction = new EventEmitter<Tag>();
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();

  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  loadingCounter = 0;

  private lastNotesFingerprint: string = '';
  private lastTagsFingerprint: string = '';

  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  allNotesByCard: NoteCard[] = [];
  cards: Card[] = [];

  isSmallScreen = false;

  constructor(
    private router: Router,
    private dataService: DataService,
  ) {}

  ngOnInit() {
    this.updateScreenSize();
    this.tags.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
    this.buildCards();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notes'] && changes['notes'].currentValue) {
      const newNotesFingerprint = this.createNotesFingerprint(this.notes);
      if (newNotesFingerprint !== this.lastNotesFingerprint) {
        this.lastNotesFingerprint = newNotesFingerprint;
        this.buildCards();
      }
    }

    if (changes['tags'] && changes['tags'].currentValue) {
      const newTagsFingerprint = this.createTagsFingerprint(this.tags);
      if (newTagsFingerprint !== this.lastTagsFingerprint) {
        this.lastTagsFingerprint = newTagsFingerprint;
        this.tags.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
        this.buildCards();
      }
    }

    if (changes['filterTagIds']) {
      this.buildCards();
    }
  }

  private createNotesFingerprint(notes: Note[]): string {
    return notes
      .map((n) => {
        const tags = Array.isArray(n.Tags)
          ? n.Tags
              .map((tagRef) => (typeof tagRef === 'string' ? tagRef : String(tagRef?._id ?? tagRef?.name ?? '')))
              .sort()
              .join(',')
          : '';
        return `${n._id}:${n.last_modified}:${n.Title ?? ''}:${n.Status ?? ''}:${tags}`;
      })
      .join('|');
  }

  private createTagsFingerprint(tags: Tag[]): string {
    return tags
      .map((t) => `${t._id}:${t.name}:${t.sortOrder}:${t.tagType}:${t.color ?? ''}:${t.isActive}`)
      .join('|');
  }

  private insertTagCards(): void {
    const activeTagIds =
      !this.filterTagIds || this.filterTagIds.length === 0 ? null : new Set(this.filterTagIds);

    for (const tag of this.tags) {
      const isActive = activeTagIds === null || activeTagIds.has(tag._id);
      if (!isActive) {
        continue;
      }

      this.cards.push({
        _id: tag._id,
        Title: tag.name,
        Type: 'tag',
        TagId: tag._id,
        SortOrder: tag.sortOrder ?? 9999,
        isActive: tag.isActive,
        isExpanded: this.getCardExpandedState(tag._id, true),
        isVisible: true,
        Color: tag.color,
      });
    }

    this.cards.sort((a, b) => (a.SortOrder ?? 9999) - (b.SortOrder ?? 9999));
  }

  toggleCardExpanded(card: Card, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    card.isExpanded = !card.isExpanded;
    this.saveCardExpandedState(card._id, card.isExpanded);
  }

  private saveCardExpandedState(cardId: string, isExpanded: boolean): void {
    try {
      const storageKey = 'tag-cards-expanded-state';
      const existingState = sessionStorage.getItem(storageKey);
      const state = existingState ? JSON.parse(existingState) : {};
      state[cardId] = isExpanded;
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save card expanded state:', error);
    }
  }

  private getCardExpandedState(cardId: string, defaultValue: boolean): boolean {
    try {
      const storageKey = 'tag-cards-expanded-state';
      const existingState = sessionStorage.getItem(storageKey);
      if (existingState) {
        const state = JSON.parse(existingState);
        return state[cardId] !== undefined ? state[cardId] : defaultValue;
      }
    } catch (error) {
      console.error('Failed to retrieve card expanded state:', error);
    }
    return defaultValue;
  }

  getCardColor(value?: string): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }

  trackByCardId(_index: number, card: Card): string {
    return card._id;
  }

  trackByNoteId(_index: number, note: NoteIdentity): string {
    return note._id ?? `note-index-${_index}`;
  }

  buildCards(): void {
    this.cards = [];
    this.allNotesByCard = [];

    this.insertTagCards();

    this.notes.forEach((note) => {
      this.resolveTagIdsForNote(note).forEach((tagId) => {
        if (!this.filterTagIds || this.filterTagIds.length === 0 || this.filterTagIds.includes(tagId)) {
          this.allNotesByCard.push({ NoteID: note._id, CardID: tagId, isActive: true });
        }
      });
    });
  }

  private resolveTagIdsForNote(note: Note): string[] {
    const rawTags = Array.isArray(note.Tags) ? note.Tags : [];
    const availableById = new Map((this.tags || []).map((tag) => [tag._id, tag]));
    const availableByName = new Map((this.tags || []).map((tag) => [tag.name.toLowerCase(), tag]));
    const availableBySlug = new Map((this.tags || []).map((tag) => [tag.slug.toLowerCase(), tag]));
    const resolved = new Set<string>();

    for (const rawTag of rawTags) {
      if (typeof rawTag === 'string') {
        const trimmed = rawTag.trim();
        if (!trimmed) {
          continue;
        }
        const byId = availableById.get(trimmed);
        if (byId?._id) {
          resolved.add(byId._id);
          continue;
        }
        const key = trimmed.toLowerCase();
        const byName = availableByName.get(key);
        const bySlug = availableBySlug.get(key);
        if (byName?._id) {
          resolved.add(byName._id);
          continue;
        }
        if (bySlug?._id) {
          resolved.add(bySlug._id);
        }
        continue;
      }

      const objectId = String(rawTag?._id ?? '').trim();
      if (objectId && availableById.has(objectId)) {
        resolved.add(objectId);
        continue;
      }

      const objectName = String(rawTag?.name ?? '').trim().toLowerCase();
      if (objectName && availableByName.has(objectName)) {
        resolved.add(availableByName.get(objectName)!._id);
        continue;
      }

      const objectSlug = String(rawTag?.slug ?? '').trim().toLowerCase();
      if (objectSlug && availableBySlug.has(objectSlug)) {
        resolved.add(availableBySlug.get(objectSlug)!._id);
      }
    }

    return Array.from(resolved);
  }

  @HostListener('window:resize')
  updateScreenSize() {
    this.isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 640;
  }

  getAttachmentFlag(note: Note): string {
    const attachments = note.Attachments;
    return Array.isArray(attachments) && attachments.length > 0 ? ' 📎' : '';
  }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

  getNotesForCard(cardId: string): Note[] {
    const noteIdSet = new Set(this.allNotesByCard.filter((nc) => nc.CardID === cardId).map((nc) => nc.NoteID));

    return this.notes
      .filter((note) => noteIdSet.has(note._id))
      .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
  }

  calculateItemCount(cardID: string): number {
    return this.allNotesByCard.filter((rec) => rec.CardID === cardID).length;
  }

  clickCardHeader(card: Card): void {
    const tag = this.tags.find((t) => t._id === card.TagId);
    if (tag) {
      this.clickTagAction.emit(tag);
    }
  }

  async createNoteForTag(tagId: string, event?: MouseEvent): Promise<void> {
    event?.stopPropagation();
    try {
      const resp = await firstValueFrom(this.dataService.createNote());
      if (resp?.status !== 'success' || !resp.data?._id) {
        console.warn('Create note did not return an _id', resp);
        return;
      }
      const newNote = resp.data;
      newNote.Tags = [tagId];
      await this.dataService.saveNoteOffline(newNote, false);
      this.router.navigateByUrl(`/edit/${encodeURIComponent(newNote._id)}`, {
        state: { filter: this.filter },
      });
    } catch (err) {
      console.error('Error creating note for tag:', err);
    }
  }

  clickRow(row: Note) {
    if (this.isSelectMode) {
      this.toggleSelection(row);
      return;
    }

    this.router.navigate(['view', row._id], {
      state: { filter: this.filter },
    });
  }

  toggleSelection(note: Note, event?: MouseEvent) {
    event?.stopPropagation();
    const id = note._id;
    if (!id) {
      return;
    }

    const isAlreadySelected = this.isNoteSelected(id);
    const updatedSelection = isAlreadySelected
      ? this.selectedNoteIds.filter((n) => n !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(updatedSelection);
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  toggleSelectAllForCard(cardId: string, event?: MouseEvent) {
    event?.stopPropagation();
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return;
    }

    const allSelected = noteIds.every((id) => this.selectedNoteIds.includes(id));

    const nextSelection = allSelected
      ? this.selectedNoteIds.filter((id) => !noteIds.includes(id))
      : Array.from(new Set([...this.selectedNoteIds, ...noteIds]));

    this.selectedNoteIdsChange.emit(nextSelection);
  }

  areAllNotesSelectedForCard(cardId: string): boolean {
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return false;
    }

    return noteIds.every((id) => this.selectedNoteIds.includes(id));
  }
}
